/**
 * F09 v2 — `/verifications` Candidates tab (§9 / E44).
 *
 * Unverified entities the evaluator surfaced (score ≥75, no risk flags).
 * Actions: Invite (optional message ≤500), Dismiss (reason + optional
 * resurface 30–365 days) — both `verifications:review` — and Direct verify
 * (super_admin, `verifications:issue_privileged`). A 409 `CANDIDATES_DISABLED`
 * (kill switch off) shows a banner instead of an error.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Chip,
  EmptyState,
  Spinner,
  Table,
  useToast,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  CANDIDATE_STATUSES,
  VERIFIABLE_ENTITY_TYPES,
  type CandidateReasonCode,
  type CandidateStatus,
  type VerifiableEntityType,
  type VerificationCandidateView,
} from '@bravemove/types';
import {
  adminVerificationApi,
  type VerificationCandidatesQuery,
  type VerificationListPage,
} from '../../api/verification';
import { AdminPagination } from '../../components/AdminPagination';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import {
  DismissCandidateDialog,
  InviteCandidateDialog,
} from '../../components/verification/DecisionDialogs';
import {
  candidateReasonLabel,
  directVerifyPath,
  entityLabel,
  entityPath,
  signalLabel,
  signalValue,
} from '../../components/verification/labels';
import {
  EntityTypeBadge,
  StrengthBadge,
  VerificationErrorBanner,
} from '../../components/verification/VerificationBits';
import { verificationErrorCode } from '../../components/verification/verificationErrors';

const PER_PAGE = 25;

interface LoadState {
  readonly key: string;
  readonly page: VerificationListPage<VerificationCandidateView> | null;
  readonly error: unknown;
}

type DialogState =
  | { readonly kind: 'invite'; readonly row: VerificationCandidateView }
  | { readonly kind: 'dismiss'; readonly row: VerificationCandidateView }
  | null;

/** Short signal summary for a candidate row (first three signal fields). */
function candidateSignals(
  t: Parameters<typeof signalLabel>[0],
  row: VerificationCandidateView,
): string {
  if (!row.signals) return t('admin:verification.queue.noSignals');
  return Object.entries(row.signals)
    .filter(([k]) => k !== 'entityType' && k !== 'risk')
    .slice(0, 3)
    .map(([k, v]) => `${signalLabel(t, k)} ${signalValue(t, v)}`)
    .join(' · ');
}

export function CandidatesTab() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { canReview, canIssue } = useVerificationPermissions();

  const [status, setStatus] = useState<CandidateStatus>('open');
  const [entityType, setEntityType] = useState<VerifiableEntityType | null>(null);
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [state, setState] = useState<LoadState>({ key: '', page: null, error: null });

  const query: VerificationCandidatesQuery = {
    status: [status],
    ...(entityType ? { entityType } : {}),
    page,
    perPage: PER_PAGE,
  };
  const queryKey = JSON.stringify(query);
  const stateKey = `${queryKey}#${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    const key = `${queryKey}#${reloadToken}`;
    adminVerificationApi.candidates(JSON.parse(queryKey) as VerificationCandidatesQuery).then(
      (result) => {
        if (!cancelled) setState({ key, page: result, error: null });
      },
      (error: unknown) => {
        if (cancelled) return;
        if (verificationErrorCode(error) === 'CANDIDATES_DISABLED') setDisabled(true);
        setState({ key, page: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [queryKey, reloadToken]);

  const loading = state.key !== stateKey;
  const rows = loading ? [] : (state.page?.items ?? []);
  const pagination = loading ? null : (state.page?.pagination ?? null);
  const listError =
    !loading && state.error && verificationErrorCode(state.error) !== 'CANDIDATES_DISABLED'
      ? state.error
      : null;

  const reload = () => setReloadToken((n) => n + 1);

  const columns: TableColumn<VerificationCandidateView>[] = [
    {
      key: 'entity',
      header: t('admin:verification.candidates.columns.entity'),
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Link
            className="font-semibold text-ink hover:underline"
            to={entityPath(r.entityType, r.entityId)}
          >
            {r.entityName}
          </Link>
          <EntityTypeBadge type={r.entityType} />
        </div>
      ),
    },
    {
      key: 'score',
      header: t('admin:verification.candidates.columns.score'),
      cell: (r) => <StrengthBadge score={r.score} riskFlags={r.riskFlags} />,
    },
    {
      key: 'reasons',
      header: t('admin:verification.candidates.columns.reasons'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.reasonCodes as readonly CandidateReasonCode[]).map((code) => (
            <Badge key={code} tone="info" size="sm">
              {candidateReasonLabel(t, code)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'signals',
      header: t('admin:verification.candidates.columns.signals'),
      cell: (r) => <span className="text-[12px] text-ink-2">{candidateSignals(t, r)}</span>,
    },
    {
      key: 'status',
      header: t('admin:verification.candidates.columns.status'),
      cell: (r) => (
        <Badge tone={r.status === 'open' ? 'info' : 'neutral'} size="sm">
          {t(`admin:verification.candidates.status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('admin:verification.common.actions'),
      align: 'end',
      cell: (r) => (
        <div className="flex flex-wrap justify-end gap-1.5">
          {canReview && r.status === 'open' ? (
            <>
              <Button
                size="xs"
                variant="secondary"
                data-testid={`candidate-invite-${r.id}`}
                onClick={() => setDialog({ kind: 'invite', row: r })}
              >
                {t('admin:verification.candidates.invite')}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                data-testid={`candidate-dismiss-${r.id}`}
                onClick={() => setDialog({ kind: 'dismiss', row: r })}
              >
                {t('admin:verification.candidates.dismiss')}
              </Button>
            </>
          ) : null}
          {canIssue ? (
            <Button
              size="xs"
              variant="ghost"
              data-testid={`candidate-direct-${r.id}`}
              onClick={() => navigate(directVerifyPath(r.entityType, r.entityId))}
            >
              {t('admin:verification.candidates.directVerify')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4" data-testid="verification-candidates-tab">
      {disabled ? (
        <div data-testid="candidates-disabled-banner">
          <Banner tone="warning" title={t('admin:verification.candidates.disabledTitle')}>
            {t('admin:verification.candidates.disabledBody')}
          </Banner>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.candidates.statusLabel')}
        </span>
        {CANDIDATE_STATUSES.map((s) => (
          <Chip
            key={s}
            selected={status === s}
            role="button"
            tabIndex={0}
            data-testid={`candidates-status-${s}`}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {t(`admin:verification.candidates.status.${s}`)}
          </Chip>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.queue.entityFilterLabel')}
        </span>
        <Chip
          selected={entityType === null}
          role="button"
          tabIndex={0}
          onClick={() => {
            setEntityType(null);
            setPage(1);
          }}
        >
          {t('admin:verification.queue.allTypes')}
        </Chip>
        {VERIFIABLE_ENTITY_TYPES.map((type) => (
          <Chip
            key={type}
            selected={entityType === type}
            role="button"
            tabIndex={0}
            data-testid={`candidates-type-${type}`}
            onClick={() => {
              setEntityType(entityType === type ? null : type);
              setPage(1);
            }}
          >
            {entityLabel(t, type)}
          </Chip>
        ))}
      </div>

      {listError ? <VerificationErrorBanner error={listError} testId="candidates-error" /> : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <>
          <Table
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            ariaLabel={t('admin:verification.candidates.tableAria')}
            empty={<EmptyState title={t('admin:verification.candidates.empty')} />}
          />
          {pagination ? (
            <AdminPagination
              pagination={pagination}
              onChange={setPage}
              shownCount={rows.length}
              testId="verification-candidates-pagination"
            />
          ) : null}
        </>
      )}

      {dialog?.kind === 'invite' ? (
        <InviteCandidateDialog
          candidateId={dialog.row.id}
          entityName={dialog.row.entityName}
          onClose={() => setDialog(null)}
          onError={(err) => {
            if (verificationErrorCode(err) !== 'CANDIDATES_DISABLED') return false;
            setDisabled(true);
            setDialog(null);
            return true;
          }}
          onDone={() => {
            setDialog(null);
            toast.push({ tone: 'success', title: t('admin:verification.toast.invited') });
            reload();
          }}
        />
      ) : null}
      {dialog?.kind === 'dismiss' ? (
        <DismissCandidateDialog
          candidateId={dialog.row.id}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast.push({ tone: 'success', title: t('admin:verification.toast.dismissed') });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
